import * as React from 'react'
import { FlatList, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import RewardConformityCard from '@components/card/RewardConformityCard'
import ConformityCard from '@components/card/ConformityTopCard'
import Button from '@components/button/Button'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as MetiersStore from '@store/metier/metier'

import api from '@api/api'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'

import Logger from '@utils/logger'

interface Props {
  manifestationId: string
}

const ITEM_WIDTH = 370

const Manif = ({ manifestationId }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [{ width }, bp] = useBreakpoint()

  const site = useReducer(SiteStore.store, (s) => s.site)

  const metiers = useReducer(MetiersStore.store, (s) => s.metiers)

  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const manifestation = React.useMemo(
    () =>
      [...manifestations.inProgress, ...manifestations.incoming, ...manifestations.toBeClosed].find(
        (p) => p.id === manifestationId
      ),
    [manifestations, manifestationId]
  )

  const [metierIds, setMetierIds] = React.useState<string[]>([])
  const [stats, setStats] = React.useState<ControleQualiteStatistiqueResponse>()
  const [metiersStats, setMetiersStats] = React.useState<Record<string, ControleQualiteStatistiqueResponse>>({})

  React.useEffect(() => {
    if (site) {
      // associated metiers
      api.controle
        .all({ manifestationIds: [manifestationId] })
        .then((controles) => setMetierIds(Array.from(new Set(controles.map((c) => c.metierId)))))
        .catch((err) => {
          setMetierIds([])
          Logger.error(err)
        })

      // manifestation stats
      api.controle
        .stats({ manifestationId, siteId: site.id })
        .then(setStats)
        .catch((err) => {
          setStats(undefined)
          Logger.error(err)
        })
    }
  }, [manifestationId, site])

  React.useEffect(() => {
    // find every metier stats
    Promise.all(metierIds.map((id) => api.controle.stats({ manifestationId, metierId: id })))
      .then((res) => {
        const mapping = res.reduce((acc, cur, idx) => {
          const id = metierIds[idx]
          acc[id] = cur

          return acc
        }, {} as Record<string, ControleQualiteStatistiqueResponse>)

        setMetiersStats(mapping)
      })
      .catch(() => setMetiersStats({}))
  }, [manifestationId, metierIds])

  if (!manifestation) {
    return (
      <Root header={i18n.t('screens.controles.title')} centered onLeft={navigation.goBack}>
        <NoContent>{i18n.t('screens.controles.form.manif.empty')}</NoContent>
      </Root>
    )
  }

  const headerLinks = [
    { label: i18n.t('screens.controles.title'), onClick: () => navigation.jumpTo('/controles') },
    { label: manifestation.libelle, onClick: () => null },
  ]

  const renderInfos = () => {
    if (!stats || !manifestation) {
      return null
    }

    if (bp === 'phone') {
      return (
        <InfosContainer>
          <InfoLightTitleContainer>
            <Name>{manifestation.libelle}</Name>
            <InfoDate>
              {i18n.t('screens.controleDetail.dates', {
                from: new Date(manifestation.dateOuverturePublic),
                to: new Date(manifestation.dateFermeturePublic),
              })}
            </InfoDate>
          </InfoLightTitleContainer>

          <TotalContainer>
            <TotalCount>{stats.nombreControle}</TotalCount>
            <Total>{i18n.t('screens.controleDetail.count', { count: stats.nombreControle })}</Total>
          </TotalContainer>

          <RewardContainer>
            <RewardConformityCard score={stats.tauxConformiteMoyen} horizontal />
          </RewardContainer>
        </InfosContainer>
      )
    }

    return (
      <>
        <SubTitle>
          {i18n.t('screens.controleDetail.dates', {
            from: new Date(manifestation.dateOuverturePublic),
            to: new Date(manifestation.dateFermeturePublic),
          })}
        </SubTitle>

        <InfosContainer>
          <TotalContainer>
            <TotalCount>{stats.nombreControle}</TotalCount>
            <Total>{i18n.t('screens.controleDetail.count', { count: stats.nombreControle })}</Total>
          </TotalContainer>

          <RewardConformityCard score={stats.tauxConformiteMoyen} />
        </InfosContainer>
      </>
    )
  }

  const renderItem = ({ item, index }: { item: string; index: number }) => {
    const metier = metiers.find((m) => m.id === item)
    const stat = metiersStats[item]

    if (!metier || !stat) {
      return null
    }

    return (
      <Card>
        <Content>
          <ItemTitle numberOfLines={1}>{metier.libelle}</ItemTitle>
          <ItemInfo>{i18n.t('screens.controleDetail.countControle', { count: stat.nombreControle })}</ItemInfo>

          <InfosContent>
            <InfoCard
              activeOpacity={0.8}
              onPress={() =>
                navigation.push(
                  `/controles/history/list?manifestationId=${manifestationId}&metierId=${item}&observation=false`
                )
              }>
              <InfoCount>{stat.nombreControleSansObservation}</InfoCount>
              <Info>{i18n.t('screens.controleDetail.noObservation')}</Info>
            </InfoCard>
            <InfoCard
              activeOpacity={0.8}
              onPress={() =>
                navigation.push(
                  `/controles/history/list?manifestationId=${manifestationId}&metierId=${item}&observation=true`
                )
              }>
              <InfoCount>{stat.nombreControleAvecObservation}</InfoCount>
              <Info>{i18n.t('screens.controleDetail.observation')}</Info>
            </InfoCard>
            <InfoCard
              activeOpacity={0.8}
              onPress={() =>
                navigation.push(
                  `/controles/history/list?manifestationId=${manifestationId}&metierId=${item}&conforme=false`
                )
              }>
              <InfoCount>{stat.nombreControleNonConforme}</InfoCount>
              <Info>{i18n.t('screens.controleDetail.noConforme', { count: stat.nombreControleNonConforme })}</Info>
            </InfoCard>
          </InfosContent>

          <Button
            label={i18n.t('screens.controleDetail.history')}
            onClick={() =>
              navigation.push(`/controles/history/list?manifestationId=${manifestationId}&metierId=${item}`)
            }
            status="active"
            color="light"
          />
        </Content>

        <Conformity>
          <ConformityCard score={stat.tauxConformiteMoyen} />
        </Conformity>
      </Card>
    )
  }

  const columns = bp === 'phone' ? 1 : Math.floor((width - 300) / ITEM_WIDTH)

  return (
    <Root header={manifestation.libelle} fixed onLeft={navigation.goBack} renderSubHeader={renderInfos}>
      {bp !== 'phone' && <Header links={headerLinks} title={manifestation.libelle} renderBottom={renderInfos} />}

      <ContentContainer>
        <SectionHeader>{i18n.t('screens.controles.form.nature.PRESTATION')}</SectionHeader>

        <FlatList
          key={columns}
          data={metierIds}
          keyExtractor={(item) => item}
          renderItem={renderItem}
          numColumns={columns}
          contentContainerStyle={{ paddingHorizontal: bp === 'phone' ? 16 : 32 }}
        />
      </ContentContainer>
    </Root>
  )
}

export default Manif

const InfosContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex-direction: row; justify-content: space-between'};
  ${(props) => props.theme.breakpoint === 'phone' && 'padding-top: 24px; padding-bottom: 16px;'};
`

const TotalCount = styled(Text)`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Title3 : props.theme.fonts.Heading1)};
  color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.background : props.theme.colors.secondaryText};
`

const Total = styled(Text)`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Footnote : props.theme.fonts.Title2)};
  color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.background : props.theme.colors.secondaryText};
`

const TotalContainer = styled(View)`
  margin-right: 24px;
`

const ContentContainer = styled(View)`
  flex: 1;
`

const Card = styled(View)`
  border-radius: 8px;
  ${(props) => props.theme.breakpoint !== 'phone' && `width: ${ITEM_WIDTH}px;`}
  margin: 8px 0;
  ${(props) => props.theme.breakpoint !== 'phone' && 'margin-right: 16px;'}
  background-color: ${(props) => props.theme.colors.background};
  ${(props) => props.theme.constants.shadow};
`

const Content = styled(View)`
  padding: 16px 20px;
`

const Conformity = styled(View)`
  position: absolute;
  width: ${(props) => (props.theme.breakpoint === 'phone' ? 120 : 150)}px;
  top: 0;
  right: 20px;
`

const ItemTitle = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  margin-right: 150px;
`

const ItemInfo = styled(Text)`
  ${(props) => props.theme.fonts.Option};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-right: 150px;
`

const InfosContent = styled(View)`
  flex-direction: row;
  padding: 16px 0;
`

const InfoCard = styled(TouchableOpacity)`
  background-color: ${(props) => props.theme.colors.cardSecondary};
  padding: 8px;
  align-items: center;
  flex: 1;
  margin: 0 8px;
`

const InfoCount = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
  font-size: 24px;
  line-height: 28px;
  margin-bottom: 8px;
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Caption1};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
`

const Name = styled(Text)`
  ${(props) => props.theme.fonts.Title3};
  color: ${(props) => props.theme.colors.linkDisabled};
`

const InfoDate = styled(Text)`
  ${(props) => props.theme.fonts.Caption1};
  color: ${(props) => props.theme.colors.linkDisabled};
`

const RewardContainer = styled(View)`
  position: absolute;
  bottom: 0;
  right: 0;
`

const InfoLightTitleContainer = styled(View)`
  margin-bottom: 24px;
`

const SubTitle = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 24px;
`

const SectionHeader = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin: 16px ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 32)}px;
`

const NoContent = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  text-align: center;
  margin: 24px 0;
`
